
import { textDiv, getColor, imageUrl, buttonString, textString } from './texts.js';
import Logger from './logger.js';
import events from './event.js';
import _ from './translate.js';
import Table from './table.js';
import products from './products.js';
import Storage from './storage.js';
import HelpPopup from './HelpPopup.js';
import { getPopupProduct, getShopUrl, emptyWindowStage } from './popupSpec.js';


class UploadStep {
    dropzoneBlocker = null;
    customizerContentDiv = null;
    cropWrapper = null;
    wrapperDiv = null;
    table = null;
    selectPaperDiv = null;
    uploadButton = null;

    constructor(customizerContentDiv, cropWrapper = null, preselectPaper = null, preselectPresentation = null) {
        this.customizerContentDiv = customizerContentDiv;
        this.cropWrapper = cropWrapper;
        this.preselectPaper = preselectPaper;
        this.preselectPresentation = preselectPresentation;
        // The shop's presentation, kept until the next open (reInitUpload clears preselectPresentation, and
        // runs after the preselect on a first open): Navigate asks for it when the upload is done.
        this.shopPresentation = preselectPresentation;
        if (!this.customizerContentDiv) {
            Logger.error('Upload step failed to load.');
            return;
        }
        this.init();
    }


    async init() {
        if (this.wrapperDiv) {
            // If already initialized, just show the wrapper, handled by CSS
            return;
        }

        this.wrapperDiv = document.createElement('div');
        this.wrapperDiv.classList.add('mscp-upload-wrapper');
        // this.wrapperDiv.style.position = 'relative';

        // Skapa wrapper för pappersvalstabell
        this.selectPaperDiv = document.createElement('div');
        this.selectPaperDiv.classList.add('mscp-upload-paper-list');
        this.wrapperDiv.appendChild(this.selectPaperDiv);

        // Skapa och rendera pappersvalstabell (egen storage-key)
        const papers = await products.getPapers();
        this.table = new Table({
            wrapperDiv: this.selectPaperDiv,
            terms: papers,
            type: 'uploadPaper', // unik storage-key
            postCreateTitleCallback: async (term) => {
                // Visa ev. pris
                return null;
            },
            selectCallback: (el, value, oldValue) => {
                this.updateUploadButtonState();
                if (this.helpPopup) this.helpPopup.hide();
                events.trigger('setPaperSelection', value); // Notify other modules of paper selection
            }
        });

        events.add('reInitUpload', () => {
            this.table.deselectAll();
            Storage.save('uploadPaper', '');
            Storage.save('uploadPresentation', '');
            this.selectPaperDiv.style.display = ''; // list is needed again until a product is chosen
            this.updateUploadButtonState();
        });

        // Deep link from the shop (?paper=<term value>): select the product as a click would.
        // Fires on reopen via the event; on first open via the constructor argument.
        // #735 (Jonas 2026-10-14: "om man redan valt det när man klickar på produkten så ja"):
        // the product is already chosen, so the list is hidden. Without a valid preselect it stays.
        const applyPreselect = async (value, presentation = null, attempt = 0) => {
            if (!this.table.clickItemByValue(value) && value && attempt < 4) {
                // Reopened for another product: the paper rows are still filtered by the previous
                // choice and unhide a moment later, so the first click finds the row hidden. Try again.
                setTimeout(() => applyPreselect(value, presentation, attempt + 1), 250);
                return;
            }
            if (this.table.items.some((i) => i.rowDiv.getAttribute('data-value') == value && i.rowDiv.classList.contains('selected'))) {
                this.selectPaperDiv.style.display = 'none';
                // First open: Paper registers its setPaperSelection listener after this step has
                // already fired, so the paper never reached storage and Presentation listed every
                // presentation (canvas: wrong options, no sizes). Store it directly.
                Storage.save('paper', value);
                // ?presentation=<slug> (only together with a valid paper): remembered here, applied by
                // Navigate when the upload is done so the Presentation step is skipped. A value that is
                // not a presentation of this paper is a no-op (the step is shown as usual).
                Storage.save('uploadPresentation', '');
                if (presentation) {
                    const valid = await products.getAttributeHierarchy(value);
                    if (valid.includes(presentation)) {
                        Storage.save('uploadPresentation', presentation);
                    }
                }
                // The refresh queued by the preselect event can run before a retried click has stored
                // the paper (reopen): render again now that it is stored.
                this.preselectPresentation = presentation;
                this.renderProduct?.();
            }
        };
        events.add('preselectUploadPaper', applyPreselect);
        if (this.preselectPaper) {
            applyPreselect(this.preselectPaper, this.preselectPresentation);
        }

        // Hitta dropzoneDiv (från upload.js) om den finns
        this.dropzoneDiv = document.querySelector('.mscp-dropzone');

        // 5. Skapa blocker/overlay för dropzone
        if (this.dropzoneDiv && !this.dropzoneDiv.querySelector('.mscp-dropzone-blocker')) {
            this.dropzoneBlocker = document.createElement('div');
            this.dropzoneBlocker.classList.add('mscp-dropzone-blocker');
            this.dropzoneBlocker.style.display = 'none';
            this.dropzoneBlocker.style.position = 'absolute';
            this.dropzoneBlocker.style.top = '0';
            this.dropzoneBlocker.style.left = '0';
            this.dropzoneBlocker.style.width = '100%';
            this.dropzoneBlocker.style.height = '100%';
            this.dropzoneBlocker.style.zIndex = '20';
            this.dropzoneBlocker.style.cursor = 'not-allowed';
            this.dropzoneDiv.appendChild(this.dropzoneBlocker);

            // Klick på overlayn togglar disabled-popup
            this.dropzoneBlocker.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.helpPopup) this.helpPopup.toggle(this.uploadButton);
            });
        }

        this.createUploadButton();
        this.customizerContentDiv.appendChild(this.wrapperDiv);

        // Nytt steg 1: product row, headline, VÄLJ BILD, checklist and an empty picture window.
        this.productBlock = document.createElement('div');
        this.productBlock.classList.add('mscp-up2');
        this.wrapperDiv.appendChild(this.productBlock);
        const refresh = () => this.renderProduct();
        // The shop link names the presentation; Storage only gets it after an async check, so keep it here.
        events.add('preselectUploadPaper', (paper, presentation = null) => {
            this.preselectPaper = paper;
            this.preselectPresentation = presentation;
            this.shopPresentation = presentation;
            setTimeout(refresh, 0);
        });
        events.add('getShopPresentation', async () => {
            if (!this.preselectPaper) return null; // classic flow: never skip the Presentation step
            if (this.shopPresentation) return this.shopPresentation;
            // A product without a presentation in its link (canvas): when the paper has only one, that is it.
            const paper = Storage.get('uploadPaper') || this.preselectPaper;
            const all = paper ? await products.getAttributeHierarchy(paper) : [];
            return all.length === 1 ? all[0] : null;
        });
        events.add('reInitUpload', () => {
            this.preselectPresentation = null;
            setTimeout(refresh, 0);
        });
        events.add('dropzoneCreated', refresh);
        this.initDragOverlay();
        refresh();

        // 5. Visa texten under tabellen
        textDiv(this.wrapperDiv, 'upload_general_text');


        // 6. Info-knapp (nu som syskon till upload-knappen, ej child till wrapper)
        const infoButton = document.createElement('div');
        infoButton.classList.add('mscp-upload-info-button', 'mscp-button');
        infoButton.style.border = 'none';
        infoButton.style.background = 'none';
        infoButton.addEventListener('click', () => {
            events.trigger('openAdditionalInfoPopup', {
                text_prop_name: 'upload_info_text'
            });
        });

        const infoButtonLabel = document.createElement('span');
        infoButtonLabel.classList.add('mscp-upload-info-button-label', 'mscp-button-label');
        infoButtonLabel.innerHTML = buttonString('upload_navigation_label');
        infoButton.appendChild(infoButtonLabel);

        const infoButtonIcon = document.createElement('span');
        infoButtonIcon.classList.add('mscp-upload-info-button-icon', 'icon-wrapper');
        infoButtonIcon.innerHTML = mindstore_object?.icon?.info || '';
        infoButton.appendChild(infoButtonIcon);

        this.wrapperDiv.appendChild(infoButton);

        // Initiera knappens state
        this.updateUploadButtonState();

        // Add a flex spacer before the logo to push it to the bottom if space allows
        const spacer = document.createElement('div');
        spacer.classList.add('mscp-upload-content-spacer');
        this.wrapperDiv.appendChild(spacer);

        // Footer logo (always last in wrapper, styled in SCSS)
        const logoUrl = imageUrl('upload_footer_logo');
        if (logoUrl) {
            const logoFooter = document.createElement('div');
            logoFooter.classList.add('mscp-upload-footer-logo');
            const logoImg = document.createElement('img');
            logoImg.src = logoUrl;
            logoImg.alt = 'Footer Logo';
            logoFooter.appendChild(logoImg);
            this.wrapperDiv.appendChild(logoFooter);
        }

    }

    /** The product chosen in the shop (paper + presentation), or null when the sheet has none for it. */
    currentProduct() {
        // The shop's choice (this.preselectPaper) backs up Storage: other steps reset Storage asynchronously
        // while the popup opens, and the first open must not lose the product to that race.
        // Only a popup opened from the shop (it names a paper) uses the new flow. A visitor who picks the paper
        // in the classic list on a product page keeps the classic flow.
        if (!this.preselectPaper) return null;
        const paper = Storage.get('uploadPaper') || Storage.get('paper') || this.preselectPaper;
        if (!paper) return null;
        const pres = Storage.get('uploadPresentation') || this.preselectPresentation || '';
        return getPopupProduct(paper, pres);
    }

    renderProduct() {
        const product = this.currentProduct();
        const block = this.productBlock;
        this.wrapperDiv.classList.toggle('mscp-up2-on', !!product);
        document.querySelector('.mscp-popup-content')?.classList.toggle('mscp-up2-on', !!product);
        if (!block) return;
        block.innerHTML = '';
        // The empty window sits in the dropzone (preview area) and opens the file picker with it.
        const dz = document.querySelector('.mscp-dropzone');
        if (dz) {
            dz.querySelector('.mscp-pv')?.remove();
            if (product) dz.appendChild(emptyWindowStage(product));
        }
        if (!product) return;

        // Step 2 preview button (the sheet's wording)
        const adj = document.querySelector('.mscp-adjust-button-text');
        if (adj) adj.textContent = 'REDIGERA';

        const row = document.createElement('div');
        row.className = 'mscp-up2__product';
        const info = document.createElement('div');
        const name = document.createElement('div');
        name.className = 'mscp-up2__name';
        name.textContent = `FOTOTAVLA | ${product.name.toUpperCase()}`;
        const sizes = document.createElement('div');
        sizes.className = 'mscp-up2__sizes';
        sizes.textContent = product.sizes;
        info.append(name, sizes);
        const swap = document.createElement('a');
        swap.className = 'mscp-up2__swap';
        swap.href = getShopUrl();
        swap.textContent = 'BYT';
        row.append(info, swap);

        const h = document.createElement('h2');
        h.className = 'mscp-up2__title';
        h.textContent = 'SNYGGT, NU SKAPAR VI DIN TAVLA!';

        const pick = document.createElement('button');
        pick.type = 'button';
        pick.className = 'mscp-up2__pick';
        pick.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 5h6M19 2v6"/><path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/><circle cx="9" cy="9" r="2"/></svg><span>VÄLJ BILD</span>';
        pick.addEventListener('click', () => this.uploadButton?.click());

        const list = document.createElement('ul');
        list.className = 'mscp-up2__list';
        ['Mobilbilder fungerar utmärkt', 'Du väljer storlek och beskär bilden i nästa steg', 'Vi kontrollerar alltid upplösningen innan tryck'].forEach((t) => {
            const li = document.createElement('li');
            li.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
            li.append(document.createTextNode(t));
            list.appendChild(li);
        });
        block.append(row, h, pick, list);
    }

    /** "SLÄPP BILDEN HÄR": a file dragged over the popup shows an overlay that takes the drop. */
    initDragOverlay() {
        const popup = this.customizerContentDiv.closest('.mscp-popup-content');
        if (!popup || popup.querySelector('.mscp-drop-overlay')) return;
        const ov = document.createElement('div');
        ov.className = 'mscp-drop-overlay';
        ov.innerHTML = '<span>SLÄPP BILDEN HÄR</span>';
        popup.appendChild(ov);
        const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
        const active = () => popup.dataset.activeStep === 'upload' && popup.classList.contains('mscp-up2-on');
        popup.addEventListener('dragenter', (e) => { if (active() && hasFiles(e)) ov.classList.add('on'); });
        ov.addEventListener('dragover', (e) => e.preventDefault());
        ov.addEventListener('dragleave', (e) => { if (e.target === ov) ov.classList.remove('on'); });
        ov.addEventListener('drop', (e) => {
            e.preventDefault();
            ov.classList.remove('on');
            if (e.dataTransfer?.files?.length && Storage.get('uploadPaper')) events.trigger('dropFiles', e.dataTransfer.files);
        });
    }

    createUploadButton() {
        if (this.uploadButton) return;
        this.uploadButton = document.createElement('div');
        this.uploadButton.classList.add('mscp-upload-btn', 'mscp-button', 'mscp-centered-button');
        // Inner label span
        const uploadButtonLabel = document.createElement('span');
        uploadButtonLabel.classList.add('mscp-upload-btn-label', 'mscp-button-label');
        uploadButtonLabel.innerHTML = buttonString('upload_button_label', 'Upload Image');
        // Icon (if any)
        let uploadIcon = imageUrl('upload_button_image', '');
        let uploadButtonIcon = null;
        if (uploadIcon) {
            uploadButtonIcon = document.createElement('span');
            uploadButtonIcon.classList.add('mscp-upload-btn-icon', 'info-icon');
            uploadButtonIcon.innerHTML = '<img src="' + uploadIcon + '" alt="Upload Icon" />';
        }
        // Build the button
        this.uploadButton.appendChild(uploadButtonLabel);
        if (uploadButtonIcon) this.uploadButton.appendChild(uploadButtonIcon);
        this.uploadButton.disabled = true;

        // Skapa återanvändbar HelpPopup för disabled helptext
        this.helpPopup = new HelpPopup({
            parentElement: this.cropWrapper || this.customizerContentDiv,
            textKey: 'upload_disabled_helptext',
            defaultText: 'Please select a paper type to enable uploading.',
        });

        this.uploadButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!this.uploadButton.disabled) {
                events.trigger('showFileUpload', true);
            } else {
                if (this.helpPopup) this.helpPopup.toggle(this.uploadButton);
            }
        });

        // Lägg till upload-knappen i cropWrapper om den finns, annars fallback
        if (this.cropWrapper) {
            this.cropWrapper.appendChild(this.uploadButton);
        } else {
            this.customizerContentDiv.appendChild(this.uploadButton);
        }
    }

    updateUploadButtonState() {
        // Kolla om ett papper är valt i uploadPaper
        const selected = Storage.get('uploadPaper');
        const activeColor = getColor('upload_button_color_active', '#000');
        const passiveColor = getColor('upload_button_color_passive', '#aaa');
        if (!this.uploadButton) {
            this.createUploadButton();
        }
        if (selected) {
            this.uploadButton.disabled = false;
            this.uploadButton.style.backgroundColor = activeColor;
            if (this.dropzoneBlocker) this.dropzoneBlocker.style.display = 'none';
        } else {
            this.uploadButton.disabled = true;
            this.uploadButton.style.backgroundColor = passiveColor;
            if (this.dropzoneBlocker) this.dropzoneBlocker.style.display = 'block';
        }
    }



}
export default UploadStep;